// <s-feed> is the TikTok-style movie feed: the slide in view plays its preview, the rest pause.
// Sound: previews try to play with sound. Phones block that until the viewer touches the page, so a blocked
// preview plays muted ("auto-muted") and the first tap anywhere turns sound on. Tapping the video or the sound
// button toggles sound for the whole feed; the choice is remembered for this visit.
class SFeed extends HTMLElement {
  #ac;
  #observer;
  #current = null;
  #currentEmbed = null;
  // Sound is on by default on every visit; tapping the video or sound button can still mute it.
  #soundOn = true;

  connectedCallback() {
    this.#ac = new AbortController();
    const feeds = [...this.querySelectorAll('[data-feed]')];
    const slides = [...this.querySelectorAll('[data-slide]')];
    if (!feeds.length) return;
    const signal = this.#ac.signal;

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const video = entry.target.querySelector('video');
        const embed = entry.target.querySelector('iframe[data-feed-embed]');
        if (!video && !embed) continue;
        // Load a slide's preview only as it comes into view, and get the next one ready so swiping is instant.
        if (entry.isIntersecting) this.#prepare(entry.target, false);
        if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
          const next = entry.target.nextElementSibling;
          if (next?.matches('[data-slide]')) this.#prepare(next, true);
          if (video) {
            this.#current = video;
            this.#currentEmbed = null;
            this.#play(video);
          } else {
            this.#current = null;
            this.#currentEmbed = embed;
            this.#playEmbed(embed);
          }
          this.#syncUi();
        } else {
          if (video) video.pause();
          if (embed) this.#sendEmbed(embed, 'pause');
        }
      }
    }, { threshold: [0, 0.6, 1] });
    slides.forEach((slide) => this.#observer.observe(slide));

    // Feed tabs (For You / Free / Trending): show one feed, pause the rest, start the new feed from the top.
    const tabs = [...this.querySelectorAll('[role="tab"]')];
    const selectTab = (tab) => {
      tabs.forEach((t) => {
        const selected = t === tab;
        t.setAttribute('aria-selected', String(selected));
        t.tabIndex = selected ? 0 : -1;
        const panel = this.querySelector(`#${t.getAttribute('aria-controls')}`);
        if (!panel) return;
        panel.hidden = !selected;
        if (selected) panel.scrollTop = 0;
        else {
          panel.querySelectorAll('video').forEach((video) => video.pause());
          panel.querySelectorAll('iframe[data-feed-embed]').forEach((embed) => this.#sendEmbed(embed, 'pause'));
        }
      });
    };
    this.addEventListener('click', (event) => {
      const tab = event.target.closest('[role="tab"]');
      if (tab) selectTab(tab);
    }, { signal });
    this.addEventListener('keydown', (event) => {
      const current = event.target.closest('[role="tab"]');
      if (!current || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      const rtl = getComputedStyle(this).direction === 'rtl';
      const forward = (event.key === 'ArrowRight') !== rtl;
      const next = tabs[(tabs.indexOf(current) + (forward ? 1 : tabs.length - 1)) % tabs.length];
      selectTab(next);
      next.focus();
    }, { signal });

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-sound], [data-slide] video, [data-embed-tap], [data-unmute]')) {
        const turnOn = this.#current ? this.#current.muted : this.#currentEmbed?.dataset.muted !== 'false';
        this.#setSound(turnOn);
      }
    }, { signal });

    // Any other first touch counts as permission to play sound on an auto-muted preview.
    const unlockSound = (event) => {
      if (event.target.closest?.('[data-sound], [data-slide] video, [data-embed-tap], [data-unmute]')) return;
      if (this.#soundOn && this.#current?.dataset.autoMuted) this.#setSound(true);
      else if (this.#soundOn && this.#currentEmbed && this.#currentEmbed.dataset.muted !== 'false') this.#setSound(true);
    };
    for (const type of ['pointerup', 'touchend', 'click', 'keydown']) {
      document.addEventListener(type, unlockSound, { signal, passive: true });
    }
  }

  // Shows the slide's cover and lets its preview video download (start it now when it's the next slide).
  #prepare(slide, start) {
    const video = slide.querySelector('video');
    if (!video) return;
    if (video.dataset.poster) {
      video.poster = video.dataset.poster;
      delete video.dataset.poster;
    }
    const stream = video.closest('s-stream');
    if (stream?.start) {
      // Adaptive stream: starts at a quality the connection can handle, then steps up.
      video.preload = 'auto';
      stream.start();
      return;
    }
    if (video.preload !== 'auto') {
      video.preload = 'auto';
      if (start && video.readyState === 0) video.load();
    }
  }

  #play(video) {
    video.muted = !this.#soundOn;
    const attempt = video.play();
    if (!attempt) return;
    attempt.then(() => {
      if (!video.muted) delete video.dataset.autoMuted;
      this.#syncUi();
    }).catch((error) => {
      if (video !== this.#current) return;
      // Interrupted because the stream took over the video: just keep playing, sound unchanged.
      if (error?.name === 'AbortError') {
        video.play().catch(() => {});
        return;
      }
      video.muted = true;
      video.dataset.autoMuted = 'true';
      video.play().catch(() => {});
      this.#syncUi();
    });
  }

  // Linked (YouTube / Vimeo) previews: load when first shown (they start muted by themselves), then play/pause/sound
  // through the players' message channel.
  #playEmbed(embed) {
    if (!embed.getAttribute('src')) {
      embed.addEventListener('load', () => {
        if (this.#soundOn && navigator.userActivation?.hasBeenActive && embed === this.#currentEmbed) {
          setTimeout(() => this.#sendEmbed(embed, 'unmute'), 600);
        }
      }, { once: true });
      embed.src = embed.dataset.src;
      embed.dataset.muted = 'true';
      return;
    }
    this.#sendEmbed(embed, 'play');
    if (this.#soundOn && navigator.userActivation?.hasBeenActive) this.#sendEmbed(embed, 'unmute');
  }

  #sendEmbed(embed, action) {
    const target = embed.contentWindow;
    if (!target || !embed.getAttribute('src')) return;
    if (action === 'mute' || action === 'unmute') embed.dataset.muted = String(action === 'mute');
    if (embed.dataset.feedEmbed === 'youtube') {
      const func = { play: 'playVideo', pause: 'pauseVideo', mute: 'mute', unmute: 'unMute' }[action];
      target.postMessage(JSON.stringify({ event: 'command', func, args: [] }), '*');
      if (action === 'unmute') target.postMessage(JSON.stringify({ event: 'command', func: 'setVolume', args: [100] }), '*');
    } else {
      const message = {
        play: { method: 'play' },
        pause: { method: 'pause' },
        mute: { method: 'setVolume', value: 0 },
        unmute: { method: 'setVolume', value: 1 },
      }[action];
      target.postMessage(JSON.stringify(message), '*');
      if (action === 'unmute') target.postMessage(JSON.stringify({ method: 'setMuted', value: false }), '*');
    }
    this.#syncUi();
  }

  #setSound(on) {
    this.#soundOn = on;
    sessionStorage.setItem('horhy-sound', on ? 'on' : 'off');
    this.querySelectorAll('video').forEach((video) => {
      video.muted = !on;
      delete video.dataset.autoMuted;
    });
    if (this.#current && this.#current.paused) this.#current.play().catch(() => {});
    if (this.#currentEmbed) this.#sendEmbed(this.#currentEmbed, on ? 'unmute' : 'mute');
    this.#syncUi();
  }

  #syncUi() {
    const audible = this.#current ? !this.#current.muted : this.#currentEmbed?.dataset.muted === 'false';
    this.querySelectorAll('[data-sound]').forEach((button) => button.setAttribute('aria-pressed', String(audible)));
    this.querySelectorAll('[data-unmute]').forEach((pill) => { pill.hidden = audible; });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }
}

if (!customElements.get('s-feed')) customElements.define('s-feed', SFeed);
